/**
 * The Worktop grid — the user's pinned arches, arranged like a phone home screen.
 *
 * Two rules drive the interaction, both of them deliberate departures from a
 * plain sortable list:
 *
 * 1. **Rearranging is a MODE, entered by a long press** (Apple's model). Outside
 *    the mode a card is only a link: no drag sensor is attached at all, so an
 *    imprecise click can no longer nudge an icon out of place. Inside the mode
 *    the cards wobble, a Done bar appears, and a click no longer navigates.
 * 2. **A move never shifts icons across rows.** See {@link computeDragPlacements}
 *    — the displacement is contained inside the destination row, and the one
 *    icon that a full row can't hold takes the cell the dragged icon vacated.
 *    The previous `arrayMove` reindexed the whole flattened list, so dropping an
 *    icon in row 1 could reflow every row beneath it.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { Check, GripHorizontal, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { UserFavoriteWithArche, WorktopLayout } from '@/lib/api';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { FavoriteCard, FavoriteIcon } from './FavoriteCard';

/** Column counts per layout. Each layout keeps its own independent position memory. */
const MOBILE_COLS = 5;
const DESKTOP_COLS = 7;
/** Minimum rows always visible */
const MIN_ROWS = 7;
/** Extra empty rows below the last occupied cell */
const EXTRA_ROWS = 2;

interface FavoritesGridProps {
  favorites: UserFavoriteWithArche[];
  onReorder: (placements: { archId: string; position: number }[], layout: WorktopLayout) => void;
}

export interface Placement {
  archId: string;
  position: number;
}

/**
 * Pure placement math for a drag, isolated from the DnD library so the
 * persistence contract is unit-testable. `posOf` selects the active layout's
 * position field, so every returned position — and therefore everything
 * persisted — is scoped to that one layout; the other layout's column is never
 * touched. Returns null for a no-op drag.
 *
 * The placement rules, in the order they are tried:
 *
 * - **Empty destination cell** → only the dragged icon moves.
 * - **Same row** → the run between the source and the destination rotates by one
 *   (drag right: the icons in between slide left; drag left: they slide right).
 *   Nothing outside the row moves, and nothing is pushed out of it.
 * - **Different row** → the icons from the destination cell rightwards shift one
 *   cell right, into the row's first free slot. If the row has no free slot, the
 *   icon that would fall off the end takes the cell the dragged icon vacated —
 *   an exchange between exactly two cells, instead of a cascade down the grid.
 */
export function computeDragPlacements(
  favorites: UserFavoriteWithArche[],
  draggedArchId: string,
  overId: string,
  posOf: (f: UserFavoriteWithArche) => number,
  cols: number,
): Placement[] | null {
  const dragged = favorites.find((f) => f.archId === draggedArchId);
  if (!dragged) return null;
  const from = posOf(dragged);

  const target = overId.startsWith('empty_')
    ? Number.parseInt(overId.slice('empty_'.length), 10)
    : (() => {
        const over = favorites.find((f) => f.archId === overId);
        return over ? posOf(over) : Number.NaN;
      })();
  if (!Number.isInteger(target) || target < 0 || target === from) return null;

  // Occupancy AFTER the dragged icon leaves its cell — every rule below reads
  // this, so a same-row move sees its own source cell as free.
  const occupied = new Map<number, string>();
  for (const f of favorites) {
    if (f.archId === draggedArchId) continue;
    occupied.set(posOf(f), f.archId);
  }

  const moved = new Map<string, number>([[draggedArchId, target]]);
  const rowStart = Math.floor(target / cols) * cols;
  const rowEnd = rowStart + cols - 1;

  if (occupied.has(target)) {
    if (from >= rowStart && from <= rowEnd) {
      // Same row: rotate the run between source and destination.
      const step = from < target ? -1 : 1;
      const [lo, hi] = from < target ? [from + 1, target] : [target, from - 1];
      for (let p = lo; p <= hi; p++) {
        const id = occupied.get(p);
        if (id) moved.set(id, p + step);
      }
    } else {
      // Different row: shift right into the row's first free slot at/after the
      // destination; with no free slot, the row's last icon takes the vacated cell.
      let free = -1;
      for (let p = target; p <= rowEnd; p++) {
        if (!occupied.has(p)) {
          free = p;
          break;
        }
      }
      const shiftFrom = free === -1 ? rowEnd : free - 1;
      if (free === -1) {
        const displaced = occupied.get(rowEnd);
        if (displaced) moved.set(displaced, from);
      }
      for (let p = shiftFrom; p >= target; p--) {
        const id = occupied.get(p);
        if (id) moved.set(id, p + 1);
      }
    }
  }

  // Persist the whole set: unchanged icons report their current position, so the
  // server always receives a complete, self-consistent picture of the layout.
  return favorites.map((f) => ({
    archId: f.archId,
    position: moved.get(f.archId) ?? posOf(f),
  }));
}

export function FavoritesGrid({ favorites, onReorder }: FavoritesGridProps) {
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  // Rearrange ("jiggle") mode. Entered by long-pressing any icon, left via Done,
  // Escape, or a click on the empty grid area.
  const [arranging, setArranging] = useState(false);
  const isDesktop = useIsDesktop();

  // Active layout drives both the column count and which position field we read/write.
  const cols = isDesktop ? DESKTOP_COLS : MOBILE_COLS;
  const layout: WorktopLayout = isDesktop ? 'desktop' : 'mobile';
  const posOf = (f: UserFavoriteWithArche) => (isDesktop ? f.desktopPosition : f.mobilePosition);

  // Only meaningful inside rearrange mode (the cards are `disabled` outside it,
  // so nothing activates). A few px of slop still separates a drag from a tap.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 80, tolerance: 8 } }),
  );

  useEffect(() => {
    if (!arranging) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setArranging(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [arranging]);

  const enterArranging = useCallback(() => setArranging(true), []);

  // Sorted for consistent ordering
  const sortedFavorites = [...favorites].sort((a, b) => posOf(a) - posOf(b));
  const sortableIds = sortedFavorites.map((f) => f.archId);

  // Position → favorite lookup (within the active layout)
  const positionMap = new Map<number, UserFavoriteWithArche>();
  for (const fav of favorites) {
    positionMap.set(posOf(fav), fav);
  }

  // Grid size
  const maxPosition = favorites.length > 0 ? Math.max(...favorites.map(posOf)) : -1;
  const minCells = MIN_ROWS * cols;
  const cellsNeeded = maxPosition + 1 + EXTRA_ROWS * cols;
  const cellCount = Math.ceil(Math.max(minCells, cellsNeeded) / cols) * cols;

  const activeFavorite = activeDragId ? favorites.find((f) => f.archId === activeDragId) : null;

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // `posOf`/`layout`/`cols` are all derived from the active breakpoint, so the
    // placements and the persisted layout always agree.
    const placements = computeDragPlacements(
      favorites,
      active.id as string,
      String(over.id),
      posOf,
      cols,
    );
    if (placements) onReorder(placements, layout);
  };

  if (favorites.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Star className="h-12 w-12 text-muted-foreground/30 mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">Your worktop is empty</h3>
        <p className="text-muted-foreground max-w-md">
          Click the star icon on any arche to add it to your worktop for quick access.
        </p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {/* Mode bar. Out of rearrange mode it is a quiet, discoverable equivalent of
          the long press (a long press is invisible to anyone who doesn't try it);
          inside it, the way out. */}
      <div className="mx-auto mb-3 flex max-w-xl items-center justify-end lg:max-w-3xl">
        {arranging ? (
          <Button
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs"
            onClick={() => setArranging(false)}
          >
            <Check className="h-3.5 w-3.5" /> Done
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs text-muted-foreground"
            title="Rearrange icons (or press and hold one)"
            onClick={enterArranging}
          >
            <GripHorizontal className="h-3.5 w-3.5" /> Arrange
          </Button>
        )}
      </div>
      <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
        <div
          className="grid grid-cols-5 lg:grid-cols-7 gap-x-3 sm:gap-x-6 gap-y-4 max-w-xl lg:max-w-3xl mx-auto"
          // A click that lands on the grid's own padding (not on a card) leaves
          // rearrange mode, the way tapping the wallpaper does on a home screen.
          onClick={(e) => {
            if (arranging && e.target === e.currentTarget) setArranging(false);
          }}
        >
          {Array.from({ length: cellCount }, (_, cellIndex) => {
            const fav = positionMap.get(cellIndex);
            if (fav) {
              return (
                <FavoriteCard
                  key={fav.archId}
                  favorite={fav}
                  arranging={arranging}
                  onLongPress={enterArranging}
                />
              );
            }
            return (
              <EmptyCell
                key={`empty_${cellIndex}`}
                id={`empty_${cellIndex}`}
                isDragActive={activeDragId !== null}
              />
            );
          })}
        </div>
      </SortableContext>

      <DragOverlay dropAnimation={null}>
        {activeFavorite ? (
          <div className="w-20 flex flex-col items-center">
            <FavoriteIcon favorite={activeFavorite} />
            <span className="mt-2 text-xs font-medium text-foreground text-center line-clamp-2 w-full px-1">
              {activeFavorite.archeName}
            </span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

// =============================================================================
// Empty grid cell — standalone droppable (not sortable)
// =============================================================================

function EmptyCell({ id, isDragActive }: { id: string; isDragActive: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={`min-h-[5rem] rounded-2xl transition-colors ${
        isDragActive ? 'border-2 border-dashed border-border/30' : ''
      } ${isOver ? 'bg-primary/10 border-primary/40!' : ''}`}
    />
  );
}
